import { z } from 'zod';

export const ulidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Expected ULID');
export type Ulid = z.infer<typeof ulidSchema>;

/** Cognito `sub` values are UUID v4/v7 strings, not ULIDs. Use this for all user-identity keys. */
export const userIdSchema = z.string().uuid();
export type UserId = z.infer<typeof userIdSchema>;

export const isoDateTimeSchema = z.string().datetime({ offset: true });
export type IsoDateTime = z.infer<typeof isoDateTimeSchema>;

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected ISO date YYYY-MM-DD');
export type IsoDate = z.infer<typeof isoDateSchema>;

/**
 * Money on the wire is a non-negative decimal-string of minor units, e.g. "15000".
 * JSON cannot natively encode bigint; we use string at the boundary and bigint in code.
 */
export const minorUnitsWireSchema = z
  .union([
    z.string().regex(/^(0|[1-9]\d*)$/, 'Expected non-negative integer string'),
    z.number().int().nonnegative(),
  ])
  .transform((value) => (typeof value === 'string' ? BigInt(value) : BigInt(value)));
export type MinorUnits = bigint;

export const minorUnitsBigintSchema = z.bigint().nonnegative().or(minorUnitsWireSchema);

export const latitudeSchema = z.number().finite().min(-90).max(90);
export type Latitude = z.infer<typeof latitudeSchema>;

export const longitudeSchema = z.number().finite().min(-180).max(180);
export type Longitude = z.infer<typeof longitudeSchema>;

export const latLngSchema = z.object({
  latitude: latitudeSchema,
  longitude: longitudeSchema,
});
export type LatLng = z.infer<typeof latLngSchema>;

export const emailSchema = z.string().email();
export type Email = z.infer<typeof emailSchema>;

export const phoneSchema = z.string().min(5).max(32);
export type Phone = z.infer<typeof phoneSchema>;

export const dayOfWeekSchema = z.number().int().min(1).max(7);
export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;

export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm');
export type TimeOfDay = z.infer<typeof timeOfDaySchema>;

/** URL-safe slug: lowercase alphanumeric segments separated by hyphens. */
export const slugSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Expected slug');
export type Slug = z.infer<typeof slugSchema>;
